import * as libRegions from '@luxuryescapes/lib-regions';
import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import { allRegions } from '../../../../consts/allRegions';
import { brandsByValue, ledBrands } from '../../../../consts/brands';
import { AVAILABILITY, ONLINE_PURCHASE } from '../../../../consts/schedule';
import { formatDateSlashesWithClock } from '../../../../services/TimeService';
import { getEarliestStart, getLatestEnd } from '../../../../utils/scheduleForType';

import { ScheduleUpdateResult } from './types';

type ListVisibilityDependencyType = typeof AVAILABILITY | typeof ONLINE_PURCHASE;
type ListVisibilityConflictReason = 'starts_before' | 'ends_after';

export interface ListVisibilityDateConflict {
  type: ListVisibilityDependencyType;
  reason: ListVisibilityConflictReason;
  limit: string;
}

const LIST_VISIBILITY_DEPENDENCY_LABELS: Record<ListVisibilityDependencyType, string> = {
  [AVAILABILITY]: 'Book By Dates',
  [ONLINE_PURCHASE]: 'Available Offline',
};

/**
 * Returns the AVAILABILITY/ONLINE_PURCHASE constraint a LIST_VISIBILITY schedule would violate,
 * if any — checks ONLINE_PURCHASE first since its window is always the tighter one when both fail.
 */
export function getListVisibilityDateConflict(
  { start, end }: { start: string | Date; end: string | Date },
  allSchedules: Array<App.Schedule>,
): ListVisibilityDateConflict | null {
  for (const type of [ONLINE_PURCHASE, AVAILABILITY] as const) {
    const dependencySchedules = allSchedules.filter((s) => s.type === type);
    if (dependencySchedules.length === 0) {
      continue;
    }

    const earliestStart = getEarliestStart(dependencySchedules);
    const latestEnd = getLatestEnd(dependencySchedules);

    if (new Date(start) < new Date(earliestStart)) {
      return { type, reason: 'starts_before', limit: earliestStart };
    }
    if (new Date(end) > new Date(latestEnd)) {
      return { type, reason: 'ends_after', limit: latestEnd };
    }
  }

  return null;
}

// States the constraint being violated, not a fix — whether to pull this schedule's dates in or
// extend the dependency first is the CM's call, not something this can infer.
export function formatListVisibilityDateConflictMessage(conflict: ListVisibilityDateConflict): string {
  const label = LIST_VISIBILITY_DEPENDENCY_LABELS[conflict.type];
  const boundary = formatDateSlashesWithClock(conflict.limit);

  return conflict.reason === 'starts_before'
    ? `List Visibility must start on or after this offer's ${label} schedule, which starts ${boundary}.`
    : `List Visibility must end on or before this offer's ${label} schedule, which ends ${boundary}.`;
}

export function extractErrorMessages(response: any) {
  return response.errors?.map((e) => e.message) ?? [response.message];
}

export function updateResultToErrorMessage(updateResult: ScheduleUpdateResult) {
  if (updateResult.result.status === 'rejected') {
    const messages = extractErrorMessages(updateResult.result.reason);
    return `${updateResult.label}: ${messages.join('; ')}`;
  }
  return null;
}

const regionNameLookup: { [regionCode: string]: string } = libRegions
  .getRegionNamesAndCode('luxuryescapes')
  .reduce((lookup, region) => {
    lookup[region.code] = region.name;
    return lookup;
  }, {});

export const getRegionName = (regionCode: string) => regionNameLookup[regionCode] ?? '';

export function getLEDBrandName(brandValue: string) {
  return ledBrands.find((b) => b.value === brandValue)?.title ?? brandValue;
}

/**
 * Builds display maps for a multi-brand schedule table (e.g. Agent Hub + CVFR).
 *
 * Returns:
 * - `labelById`   — display label for each schedule id that should appear as a row
 * - `warningById` — inline warning message for out-of-sync rows
 */
export function buildScheduleDisplayMap(
  schedules: Array<App.Schedule>,
  brandList: Array<Brand>,
  brand: Brand,
): {
  labelById: Map<number, string>;
  warningById: Map<number, string>;
} {
  const labelById = new Map<number, string>();
  const warningById = new Map<number, string>();

  // Pre-compute which regions each brand supports so we can suppress
  // "CVFR missing" warnings for regions that sub-brands don't operate in
  // (e.g. Agent Hub supports GB/US but CVFR only supports AU/NZ).
  const validRegionsByBrand = new Map<Brand, Set<string>>();
  for (const b of brandList) {
    const regions = libRegions.getRegions(b).map((r) => r.code);
    validRegionsByBrand.set(b, new Set(regions));
  }

  const groups = new Map<string, Array<App.Schedule>>();
  for (const s of schedules) {
    if (!brandList.includes(s.brand as Brand)) {
      continue;
    }
    const key = `${s.type}:${s.region}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(s);
  }

  for (const group of groups.values()) {
    const region = group[0].region;
    const regionName = allRegions.find((r) => r.code === region)?.name ?? region;
    const primarySchedule = group.find((s) => s.brand === brand);
    const allBrandsPresent = group.length === brandList.length;
    const allBrandsSupportRegion = brandList.every((b) => validRegionsByBrand.get(b)?.has(region));
    const datesEqual =
      allBrandsPresent &&
      group.every(
        (s) =>
          new Date(s.start).getTime() === new Date(group[0].start).getTime() &&
          new Date(s.end).getTime() === new Date(group[0].end).getTime() &&
          !!s.restrict_purchase_to_lux_plus_members === !!group[0].restrict_purchase_to_lux_plus_members,
      );

    if (datesEqual) {
      // All brands in sync — one row, no warning
      const representative = primarySchedule ?? group[0];
      labelById.set(representative.id, regionName);
    } else if (!primarySchedule) {
      // Only sub-brand(s) — orphaned legacy record; delete to clean up
      for (const s of group) {
        labelById.set(s.id, `${regionName} (${brandsByValue[s.brand]?.title ?? s.brand})`);
        warningById.set(s.id, 'Orphaned — no Agent Hub counterpart. Delete this row to clean up.');
      }
    } else if (!allBrandsPresent) {
      if (allBrandsSupportRegion) {
        // Only primary brand in a region all sub-brands should cover
        warningById.set(primarySchedule.id, 'CVFR schedule missing — will sync automatically on next save.');
      }
      // If the region isn't supported by all brands (e.g. GB is AH-only), show the row
      // without any warning — the backend sync will never create a sub-brand schedule here.
      labelById.set(primarySchedule.id, regionName);
    } else {
      // Both present but dates differ — backend will align CVFR on next Agent Hub save
      for (const s of group) {
        labelById.set(s.id, `${regionName} (${brandsByValue[s.brand]?.title ?? s.brand})`);
        warningById.set(
          s.id,
          'Out of sync — CVFR dates differ. Will sync on next Agent Hub save, or delete to remove.',
        );
      }
    }
  }

  return { labelById, warningById };
}
